import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Bike, Mountain, Sailboat, Ship, User, type LucideIcon } from 'lucide-react';
import { GlassCard } from '@/components/shared/GlassCard';
import { brand } from '@/lib/theme/tokens';
import { fadeUp, inViewOnce, staggerContainer } from '@/lib/motion/variants';
import { useMarketplaceStats } from '@/features/marketplace/hooks/useMarketplaceStats';
import type { MarketplaceStats } from '@/features/marketplace/types';

type Service = {
  icon: LucideIcon;
  name: string;
  count: string;
  sub: string;
  color: string;
  to: string;
  statsKey: keyof MarketplaceStats;
};

const SERVICES: Service[] = [
  { icon: Mountain, name: 'Treks', count: '142', sub: 'open this month', color: brand.teal, to: '/search?type=trek', statsKey: 'treks' },
  { icon: Ship, name: 'Stays', count: '230', sub: 'hotels + homestays', color: brand.chinar, to: '/stays', statsKey: 'stays' },
  { icon: Sailboat, name: 'Houseboats', count: '38', sub: 'on Dal & Nagin', color: brand.saffron, to: '/houseboats', statsKey: 'houseboats' },
  { icon: Bike, name: 'Rides', count: '46', sub: 'Enfields + 4WDs', color: brand.ice, to: '/vehicles', statsKey: 'rides' },
  { icon: User, name: 'Guides', count: '38', sub: 'all police-verified', color: brand.ok, to: '/guides', statsKey: 'guides' },
];

export function ServicesStrip() {
  const { data: stats } = useMarketplaceStats();

  return (
    <section className="mx-auto mt-16 max-w-7xl px-4 md:mt-20 md:px-6">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={inViewOnce}
        variants={staggerContainer}
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
      >
        {SERVICES.map((s) => {
          const Icon = s.icon;
          return (
            <motion.div key={s.name} variants={fadeUp}>
              <Link to={s.to} className="block">
                <GlassCard interactive className="flex items-center gap-3 p-4">
                  <div
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px]"
                    style={{
                      background: `linear-gradient(135deg, ${s.color}, ${s.color}90)`,
                      boxShadow: `0 8px 22px ${s.color}50`,
                    }}
                  >
                    <Icon size={20} color="#fff" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-display text-xl font-bold text-ink">{stats ? stats[s.statsKey] : s.count}</span>
                      <span className="text-sm font-semibold text-ink">{s.name}</span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-ink-mute">{s.sub}</div>
                  </div>
                  <ArrowRight size={14} className="text-ink-dim" />
                </GlassCard>
              </Link>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}
